/**
 * playerEvents: per-user client-event funnel emitter.
 *
 * Fire-and-forget instrumentation for the new "player_events" funnel, used to
 * see where new players drop off before they actually play (lobby → tile →
 * launch, and the OAuth DOB-wall). It is telemetry ONLY and must never affect
 * the UI.
 *
 * Contract with the backend:
 *   POST /api/casino/events  { event_type, session_id?, metadata? }
 *   - Auth: the shared apiClient request interceptor injects the Supabase JWT
 *     (this is a POST → always attached). player_id is derived SERVER-SIDE from
 *     that token (we NEVER send it from the client).
 *   - event_type MUST be one of the v1 whitelist below.
 *
 * Guarantees (all deliberate, all load-bearing):
 * - STRICTLY FIRE-AND-FORGET: `trackEvent` returns void synchronously. The
 *   network call runs in a detached async task that callers never await.
 * - NEVER THROWS: every path is wrapped; a storage, network, auth, or serialize
 *   failure is swallowed. Telemetry can never bubble an error into a render.
 * - NO-OP WHEN LOGGED OUT: we short-circuit if there is no access token, so a
 *   signed-out visitor never emits (and never triggers a pointless 401).
 * - NO SPIN: emits go through the raw axios instance (apiClient.getInstance()),
 *   NOT the retrying wrapper. A failed beacon is dropped, never retried.
 * - RICH FIRST-PARTY CONTEXT: metadata captures ARBITRARY event context (any
 *   keys a call site passes: paths, referrers, game codes, actions, results,
 *   device hints, timings, error codes, nested objects/arrays). This is
 *   behavioural telemetry in our own service-role-locked table, so we want to
 *   see what users actually did. Three guards only:
 *     1. SECRETS are hard-blocked. Any key whose NAME contains a secret word
 *        SEGMENT (pass, secret, token, auth, api_key, card, cvv/cvc, ssn, jwt,
 *        bearer, credential) is dropped, recursively, and never transmitted or
 *        logged anywhere. Segment-matched, so benign funnel keys that merely
 *        contain a secret word as a substring (author, cardinality) survive.
 *     2. IDENTITY PII is hard-blocked (defense-in-depth mirror of the
 *        authoritative server-side filter). Any key matching IDENTITY_KEY_RE
 *        (email, first/last/full name, dob, date of birth, phone, mobile,
 *        address, street, zip, postal, national id) is dropped recursively:
 *        identity comes from the player_id join, not from metadata.
 *     3. SIZE is bounded so the backend's 2 KiB cap never silently 422-drops an
 *        event: each string value is capped and, if the whole metadata JSON is
 *        still too big, the largest keys are dropped until it fits.
 *   Every event is auto-enriched with `page` and `referrer` (caller keys win).
 * - IDENTITY VIA JOIN, NOT DUPLICATION: every row already carries player_id
 *   (server-derived from the JWT), which joins to `players` for all identity.
 *   Call sites therefore should NOT pass identity PII (email, name, DOB,
 *   address) into metadata: duplicating it is redundant and only widens the
 *   breach/deletion surface. This is now enforced by an active identity-key
 *   filter here (IDENTITY_KEY_RE), mirroring the authoritative server-side one.
 * - DEDUPED: rapid duplicate emits (React StrictMode double-effects, re-renders)
 *   are collapsed within a short window. `session_start` is additionally guarded
 *   to at most once per tab-session via sessionStorage.
 */

import apiClient from "../utils/apiClient";
import authService from "./authService";
import logger from "../utils/logger";

const ENDPOINT = "/api/casino/events";

// Per-tab session id. Reuses the experiment sessionStorage convention
// (`cluck.<name>.v1`, see useFirstRunActivation). One id per browser tab, for
// the tab's lifetime. This is the funnel's unit of "a visit".
const SESSION_ID_KEY = "cluck.playerEvents.sessionId.v1";
const SESSION_START_PREFIX = "cluck.playerEvents.sessionStartSent.v1";

// v1 event whitelist. MUST mirror the backend. Anything not in this set is
// dropped client-side (defense in depth; the server validates too).
const ALLOWED_EVENTS = new Set([
  "session_start",
  "lobby_viewed",
  "game_tile_click",
  "game_launch_attempt",
  // Launch OUTCOME events (v1.1): did the game frame actually load after an
  // attempt? Lets us measure first-session game-load success. MUST mirror the
  // backend Zod enum (PLAYER_EVENT_TYPES) and the DB CHECK constraint.
  "game_launch_succeeded",
  "game_launch_failed",
  "dob_gate_shown",
  "dob_gate_abandoned",
  "dob_gate_completed",
  // Didit phone-gate funnel (v1.2). shown/started/abandoned fire from PhoneGate;
  // completed fires from the Didit callback once the gate clears. Lets us measure
  // the phone-verification funnel AND detect a re-wall (a `phone_gate_shown`
  // AFTER a `phone_gate_completed` = the player got bounced back to the gate).
  // MUST mirror the backend Zod enum and the DB CHECK constraint.
  "phone_gate_shown",
  "phone_gate_started",
  "phone_gate_abandoned",
  "phone_gate_completed",
  "buy_prompt_shown",
  // Checkout / deposit funnel (v1.3). The money step: which method was picked,
  // when Pay was clicked, how long the card form took to actually render, and
  // when a checkout was dismissed without completing. This is where deposits
  // die (card form "displayed" but no card entered) and we were not measuring
  // the client side of it. MUST mirror the backend Zod enum + DB CHECK.
  "checkout_viewed",
  "payment_method_selected",
  "checkout_pay_clicked",
  "card_form_loaded",
  "checkout_dismissed",
  // Card warm-up diagnostics (v1.4): why the pre-warmed session never reached
  // the client (card_warm_failed) and when we bailed a stranded player to the
  // standalone card page (card_fallback_redirect). MUST mirror the backend Zod
  // enum + DB CHECK.
  "card_warm_failed",
  "card_fallback_redirect",
  // Card-frame observability (v1.5): what actually happens between "Pay
  // clicked" and Pay.com's next webhook — frame revealed (card_frame_opened),
  // iframe never rendered (card_form_stuck), how far Pay.com's embedded app
  // booted (card_pm_message), and whether a submit ever round-tripped back to
  // a cluck.us return URL (card_return_nav). MUST mirror the backend Zod enum
  // + DB CHECK.
  "card_frame_opened",
  "card_form_stuck",
  "card_pm_message",
  "card_return_nav",
]);

// Secret-ish key blocklist. Any metadata key whose NAME matches a secret WORD
// SEGMENT is dropped (recursively). Secrets must never be logged anywhere, not
// even in our own first-party table.
//
// Matched by whole underscore/dash/camelCase segment (NOT raw substring) so we
// catch real secret keys (`access_token`, `apiKey`, `cardNumber`, `session_secret`)
// without over-matching benign funnel keys that merely CONTAIN a secret word as a
// substring (author contains "auth"; cardinality/discard contain "card"; sort_key).
const SECRET_SEGMENTS = new Set([
  "pass",
  "passwd",
  "password",
  "pwd",
  "secret",
  "secrets",
  "token",
  "tokens",
  "auth",
  "authorization",
  "authentication",
  "jwt",
  "bearer",
  "cvv",
  "cvc",
  "ssn",
  "card",
  "apikey",
  "credential",
  "credentials",
]);

// Identity-PII key denylist (defense-in-depth mirror of the authoritative
// server-side filter). Identity already lives on the `players` row via the
// server-derived player_id join, so duplicating email / name / DOB / phone /
// address into metadata is redundant and only widens the breach/deletion
// surface. Anything matching here is dropped recursively, same as secrets;
// rich NON-identity context still flows.
const IDENTITY_KEY_RE =
  /(^|_)e[-_]?mail|first[_-]?name|last[_-]?name|full[_-]?name|(^|_)dob(_|$)|date[_-]?of[_-]?birth|birth|phone|mobile|address|street|zip|postal|ssn|national[_-]?id/i;

// Split an object key into lowercase word segments so secret words are matched
// as whole tokens, never as substrings inside a legit word:
//   access_token -> [access, token]   apiKey -> [api, key]
//   cardNumber   -> [card, number]     author -> [author]  (survives)
function keySegments(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase boundary: fooBar -> foo Bar
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // APIKey -> API Key
    .split(/[^a-zA-Z0-9]+/) // split on _, -, spaces, etc.
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

// True when a key NAME denotes a secret. Whole-segment match, plus the split
// "api" + "key" composite (`api_key` / `apiKey`).
function isSecretKey(key) {
  const segs = keySegments(key);
  for (let i = 0; i < segs.length; i += 1) {
    if (SECRET_SEGMENTS.has(segs[i])) return true;
    if (segs[i] === "api" && segs[i + 1] === "key") return true;
  }
  return false;
}

// Per-string-value cap. Long free-text is truncated (not dropped) so a single
// blob can't blow the size budget on its own.
const MAX_META_VALUE_LEN = 512;

// Overall metadata budget (serialized UTF-8 bytes). Kept comfortably under the
// backend's 2048-byte hard cap so events are never silently 422-dropped; when
// exceeded we trim largest-key-first until it fits.
const MAX_METADATA_BYTES = 1800;

// Max object nesting we walk when sanitizing (guards pathological / cyclic input
// from causing unbounded recursion; the whole call is best-effort anyway).
const MAX_META_DEPTH = 6;

// Rapid-duplicate suppression window (ms). Collapses double-fires (StrictMode
// double-mount effects, rapid re-renders) into a single emit.
const DEDUPE_WINDOW_MS = 1500;
const recentEmits = new Map(); // dedupeKey -> last-emit timestamp

// ── sessionStorage helpers (can throw in private mode / when disabled) ───────
function readSession(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* ignore storage failures; telemetry is best-effort */
  }
}

function safeRandomId() {
  try {
    if (typeof globalThis.crypto?.randomUUID === "function") {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    /* fall through to the manual id */
  }
  return `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Read (or lazily create) this tab's funnel session id.
 * @returns {string}
 */
export function getSessionId() {
  const existing = readSession(SESSION_ID_KEY);
  if (existing) return existing;
  const id = safeRandomId();
  writeSession(SESSION_ID_KEY, id);
  return id;
}

/**
 * Recursively sanitize a metadata value:
 *  - drop keys whose NAME denotes a secret or identity PII (at any depth),
 *  - truncate long strings (never drop them),
 *  - keep numbers / booleans / null and nested objects/arrays as-is,
 *  - drop non-serializable values (functions, symbols, bigint) and anything
 *    below the depth guard.
 * Coerces nothing away except secrets and non-serializable values.
 */
function sanitizeValue(value, depth) {
  if (value === null) return null;
  const t = typeof value;
  if (t === "string") {
    return value.length > MAX_META_VALUE_LEN
      ? value.slice(0, MAX_META_VALUE_LEN)
      : value;
  }
  if (t === "number" || t === "boolean") return value;
  if (t === "object") {
    if (depth >= MAX_META_DEPTH) return undefined;
    if (Array.isArray(value)) {
      return value
        .map((v) => sanitizeValue(v, depth + 1))
        .filter((v) => v !== undefined);
    }
    const out = {};
    for (const key of Object.keys(value)) {
      // Secrets AND identity PII never leave, at any depth.
      if (isSecretKey(key) || IDENTITY_KEY_RE.test(key)) continue;
      const sv = sanitizeValue(value[key], depth + 1);
      if (sv !== undefined) out[key] = sv;
    }
    return out;
  }
  // functions / symbols / bigint / undefined -> not serializable, drop
  return undefined;
}

/** Serialized UTF-8 byte length of an object (falls back to char length). */
function jsonByteLength(obj) {
  const str = JSON.stringify(obj) || "";
  try {
    return new TextEncoder().encode(str).length;
  } catch {
    return str.length;
  }
}

/**
 * Trim `obj` in place until it fits MAX_METADATA_BYTES by repeatedly dropping
 * the largest top-level key. Best-effort: guarantees the event isn't 422-dropped
 * for size rather than silently losing the whole payload.
 */
function fitToBudget(obj) {
  let guard = 0;
  while (jsonByteLength(obj) > MAX_METADATA_BYTES && guard < 200) {
    const keys = Object.keys(obj);
    if (!keys.length) break;
    let largestKey = keys[0];
    let largestSize = -1;
    for (const k of keys) {
      const size = (JSON.stringify(obj[k]) || "").length;
      if (size > largestSize) {
        largestSize = size;
        largestKey = k;
      }
    }
    delete obj[largestKey];
    guard += 1;
  }
}

/** Auto page context. Guarded against SSR / private-mode / access throws. */
function pageContext() {
  const ctx = {};
  try {
    if (typeof window !== "undefined" && window.location) {
      ctx.page = `${window.location.pathname || ""}${window.location.search || ""}`;
    }
  } catch {
    /* ignore */
  }
  try {
    if (typeof document !== "undefined" && document.referrer) {
      ctx.referrer = document.referrer;
    }
  } catch {
    /* ignore */
  }
  return ctx;
}

/**
 * Build the metadata payload: auto-enrich with page context (caller keys win),
 * strip secrets, cap value sizes, and bound the total to the byte budget.
 * Returns undefined only when nothing survives.
 */
function buildMetadata(metadata) {
  const caller =
    metadata && typeof metadata === "object" && !Array.isArray(metadata)
      ? metadata
      : {};
  // Caller-supplied keys take precedence over auto-enriched page context.
  const merged = { ...pageContext(), ...caller };
  const clean = sanitizeValue(merged, 0) || {};
  fitToBudget(clean);
  return Object.keys(clean).length ? clean : undefined;
}

/**
 * The detached network task. Fully self-contained: resolves the session token,
 * no-ops when logged out, and swallows every error. Never awaited by callers.
 */
async function emit(eventType, metadata) {
  try {
    // No-op if not authenticated. player_id is taken server-side from the JWT.
    const { session } = await authService.getSession();
    if (!session?.access_token) return;

    const body = { event_type: eventType, session_id: getSessionId() };
    if (metadata) body.metadata = metadata;

    // fetch + keepalive, NOT axios. Several funnel events fire immediately
    // before the page goes away: phone_gate_started and phone_gate_completed
    // both sit one statement before a window.location.assign, and
    // checkout_dismissed fires on teardown. An XHR is cancelled when the
    // document unloads, which is why phone_gate_completed recorded ZERO rows
    // across 405 real phone verifications. keepalive is the one transport the
    // browser is required to finish after navigation starts.
    //
    // Auth is attached by hand because keepalive bypasses the axios
    // interceptors. Payloads are capped at MAX_METADATA_BYTES, far under the
    // 64KB keepalive ceiling.
    await fetch(`${apiClient.getInstance().defaults.baseURL || ""}${ENDPOINT}`, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    });
  } catch {
    // Swallow everything (network / 4xx / 5xx / auth). Best-effort telemetry.
  }
}

/**
 * Track a funnel event. Fire-and-forget: returns synchronously, never throws.
 *
 * @param {string} eventType - one of the v1 whitelist.
 * @param {Object} [metadata] - ARBITRARY event context (any keys: paths,
 *   referrers, game codes, actions, results, device hints, timings, error
 *   codes, nested objects/arrays). Auto-enriched with `page` + `referrer`
 *   (caller keys win). Secret-ish keys are hard-dropped and the payload is
 *   size-bounded. Do NOT pass identity PII (email/name/DOB/address): identity
 *   comes from the player_id join, and duplicating it only widens the breach
 *   surface.
 */
export function trackEvent(eventType, metadata) {
  try {
    if (!ALLOWED_EVENTS.has(eventType)) {
      if (import.meta.env.DEV) {
        logger.warn("[playerEvents] dropping unknown event_type", eventType);
      }
      return;
    }

    const clean = buildMetadata(metadata);

    // Rapid-duplicate suppression.
    const now = Date.now();
    const dedupeKey = `${eventType}:${clean ? JSON.stringify(clean) : ""}`;
    const last = recentEmits.get(dedupeKey);
    if (last && now - last < DEDUPE_WINDOW_MS) return;
    recentEmits.set(dedupeKey, now);

    // Opportunistic cleanup so the map can't grow unbounded.
    if (recentEmits.size > 50) {
      for (const [k, t] of recentEmits) {
        if (now - t > DEDUPE_WINDOW_MS) recentEmits.delete(k);
      }
    }

    void emit(eventType, clean);
  } catch {
    // Never let telemetry affect the caller.
  }
}

/**
 * Track `session_start` at most once per tab-session. Call when an
 * authenticated session is established (sign-in or session restore).
 * @param {Object} [metadata]
 */
export function trackSessionStart(metadata) {
  try {
    const marker = `${SESSION_START_PREFIX}.${getSessionId()}`;
    if (readSession(marker) === "1") return; // already sent this tab-session
    writeSession(marker, "1");
    trackEvent("session_start", metadata);
  } catch {
    // Never let telemetry affect the caller.
  }
}

export default { trackEvent, trackSessionStart, getSessionId };
